import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BsCart3 } from 'react-icons/bs';
import { cartService } from '../services/cartService';

const CartIcon = ({ onClick, className = '', style = {} }) => {
  const { t, i18n } = useTranslation();
  const [cartItemCount, setCartItemCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Check if current language is Arabic
  const isRTL = i18n.language === 'ar';

  const fetchCartCount = async () => {
    try {
      setLoading(true);
      const cart = await cartService.getCart();
      setCartItemCount(cart.item_count || 0);
    } catch (error) {
      // If cart doesn't exist or other error, count is 0
      setCartItemCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCartCount();
    
    // Set up an interval to periodically refresh cart count
    const interval = setInterval(fetchCartCount, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  // Create a custom event listener for cart updates
  useEffect(() => {
    const handleCartUpdate = () => {
      fetchCartCount();
    };

    // Listen for custom cart update events
    window.addEventListener('cartUpdated', handleCartUpdate);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  return (
    <div
      className={`cart-icon-container ${className}`}
      style={{
        position: 'relative',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: window.innerWidth <= 768 ? '8px' : '10px',
        borderRadius: '50%',
        backgroundColor: 'white',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.3s ease',
        border: '1px solid #e0e0e0',
        ...style
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.05)';
        e.currentTarget.style.boxShadow = '0 4px 15px rgba(145, 67, 218, 0.2)';
        e.currentTarget.style.borderColor = '#9143DA';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.1)';
        e.currentTarget.style.borderColor = '#e0e0e0';
      }}
      title={t('Shopping Cart')}
    >
      <BsCart3 
        size={window.innerWidth <= 768 ? 20 : 24} 
        color="#9143DA"
        style={{
          transition: 'color 0.3s ease',
          transform: isRTL ? 'scaleX(-1)' : 'scaleX(1)' // Flip cart icon for RTL
        }}
      />
      
      {/* Notification Badge */}
      {cartItemCount > 0 && (
        <div
          style={{
            position: 'absolute',
            top: window.innerWidth <= 768 ? '-3px' : '-5px',
            [isRTL ? 'left' : 'right']: window.innerWidth <= 768 ? '-3px' : '-5px',
            backgroundColor: '#dc3545',
            color: 'white',
            borderRadius: '50%',
            minWidth: window.innerWidth <= 768 ? '18px' : '20px',
            height: window.innerWidth <= 768 ? '18px' : '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: window.innerWidth <= 768 ? '10px' : '12px',
            fontWeight: 'bold',
            border: '2px solid white',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            animation: cartItemCount > 0 ? 'cartBadgePulse 0.3s ease-out' : 'none'
          }}
        >
          {cartItemCount > 99 ? '99+' : cartItemCount}
        </div>
      )}

      {/* Loading Indicator */}
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '16px',
            height: '16px',
            border: '2px solid #f3f3f3',
            borderTop: '2px solid #9143DA',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}
        />
      )}

      {/* CSS Animations */}
      <style>
        {`
          @keyframes cartBadgePulse {
            0% {
              transform: scale(0.8);
              opacity: 0.7;
            }
            50% {
              transform: scale(1.1);
              opacity: 1;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default CartIcon;
